<template>
  <div class="flex h-full overflow-y-hidden">
    <div class="w-1/4 min-w-64 overflow-visible">
      <app-header-bot-control :bot-id="botId" @change-bot="changeBot"/>
      <list-section :items="eventList"
                    :bot-id="botId"
                    :add-button-options="addButtonOptions"
                    :loaders="loaders"
                    :renamed-id="renamedId"
                    :selected-id="eventId"
                    item-id-name="eventId"
                    :route-props="routeProps"
                    :add-tooltip-text="isNewEvent ? 'Cancel Event' : 'Create Event'"
                    :menu-builder="menuBuilder"
                    @add="createNewEvent"
                    @cancel="cancelNewEvent"
                    @set-rename-mode="handleRenameMode"
                    @rename="saveEventName"
                    @item-selected="selectEvent"
                    @request-delete="handleRequestDelete"
                    @copy-code="handleCopy"
                    @enable-auto-translate="handleRequestEnableAutoTranslate"
      >
        <template #empty>
          <img :src="flagsImage" alt="flags image">
          <span>
            In Events, you can define how the chatbot reacts to customer actions. For reacting to customers' questions, use
            <router-link :to="{ name: ROUTE_NAMES.answers }" target="_blank" class="hover:underline">Answers</router-link>.
          </span>
        </template>

        <template #delete-item-icon>
          <delete-event-icon class="h-8 w-8 text-gray-dark"/>
        </template>
      </list-section>
    </div>

    <shadowed-wrapper class="w-3/4">
      <div v-if="isEmpty(eventList)" class="flex items-center h-full w-full justify-center">
        <img :src="noEventsImage" alt="no events image">
      </div>

      <template v-else>
        <app-header class="flex justify-between pl-10 pr-5 w-full">
          <app-header-item-control v-if="event"
                                   :item="event"
                                   :is-copied="isCopied"
                                   :is-rename-mode="isRenameModeInAppHeader"
                                   :is-loading="loaders.name"
                                   :menu-builder="menuBuilder"
                                   @copy-code="handleCopy"
                                   @set-rename-mode="handleRenameMode"
                                   @rename="saveEventName"
                                   @request-delete="handleRequestDelete"
                                   @enable-auto-translate="handleRequestEnableAutoTranslate"
          >
            <template #multi-bot-item-selector>
              <MultiBotItemSelector v-if="event && event.multiBotItemId && eventVariantList.length"
                :items="eventVariantList"
                :selected-id="eventId"
                editor-type="Event"
                @item-selected="selectEventVariant"
                class="ml-4"
              />
            </template>
          </app-header-item-control>
          <event-save-button v-if="event"
                             :event="event"
                             :bot-id="botId"
                             :is-disabled="inheritsConversation"
                             class="my-auto"
                             @process-new-event="processNewEvent"
          />
        </app-header>

        <div class="flex w-full h-full">
          <with-loader id="chat-preview-loader"
                       class="border-transparent bg-white w-7/12 min-w-136 h-full"
                       :loading="!event || loaders.conversationJsons"
                       trigger-loader-on-mount
          >
            <chat-preview class="flex flex-grow"
                          :item-id="eventId"
                          :is-read-only="inheritsConversation"
            />
          </with-loader>

          <with-loader id="editor-sidebar-loader"
                       class="border-transparent bg-white min-w-104 w-5/12 h-full"
                       :loading="!event || loaders.conversationJsons"
                       trigger-loader-on-mount
          >
            <editor-sidebar :bot-id="botId"
              :item="event"
              @edit-market="detachEvent"
            />
          </with-loader>
        </div>
      </template>
    </shadowed-wrapper>

    <delete-dialog :is-shown="isDeleteDialogShown"
                   :item="itemToDelete"
                   :delete-title="deleteTitle"
                   :delete-description="deleteDescription"
                   :delete-confirm-text="deleteConfirmText"
                   @cancel="cancelDelete"
                   @delete="deleteEvent"
    >
      <template #delete-item-icon>
        <delete-event-icon class="h-8 w-8 text-gray-dark"/>
      </template>
    </delete-dialog>

    <confirm-dialog :is-shown="isConfirmDialogShown"
                    :item="itemToEnableAutoTranslate"
                    :title="enableAutoTranslateTitle"
                    :description="enableAutoTranslateDescription"
                    :confirm-text="enableAutoTranslateConfirmText"
                    :busy-button-id="BUSY_BUTTONS.ENABLE_AUTO_TRANSLATE"
                    @cancel="cancelEnableAutoTranslate"
                    @confirm="confirmEnableAutoTranslate"
    >
      <template #icon>
        <vs-icon icon="sync" size="3rem" class="text-gray-dark" />
      </template>
    </confirm-dialog>
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader'
import AppHeaderBotControl from '@/components/common/AppHeaderBotControl'
import AppHeaderItemControl from '@/components/common/AppHeaderItemControl'
import ChatPreview from '@/components/preview/ChatPreview'
import DeleteEventIcon from '@/assets/images/icons/DeleteEventIcon'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import DeleteDialog from '@/components/common/DeleteDialog.vue'
import EditorSidebar from '@/components/editor/EditorSidebar'
import ListSection from '@/components/common/ListSection.vue'
import ShadowedWrapper from '@/components/base-components/ShadowedWrapper'
import SideListMenu from '@/components/common/SideListMenu'
import EventSaveButton from './EventSaveButton'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import templateModule from '@/store/modules/template.module'
import eventService from '@/services/amio/event.service'
import templateService from '@/services/amio/template.service'
import { ROUTE_NAMES } from '@/router'
import { CONVERSATION_TYPES } from '@/store/constants'
import { EVENT_TYPES, ITEM_IDS, ITEM_NAMES, MENU_ITEMS } from '@/utils/editor.constants'
import dropdownButtonsUtils from '@/utils/dropdown-button.utils'
import { DELETE_TEXT, ENABLE_AUTO_TRANSLATE_TEXT } from '@/utils/text.constants'
import toaster from '@/utils/toaster'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import findIndex from 'ramda/src/findIndex'
import propEq from 'ramda/src/propEq'
import values from 'ramda/src/values'
import flagsImage from '@/assets/images/flags.svg'
import noEventsImage from '@/assets/images/no_events.svg'
import sort from 'ramda/src/sort'
import WithLoader from '@/components/common/WithLoader.vue'
import botModule from '@/store/modules/bot.module'
import emailConfigService from '@/services/amio/email-config.service'
import editorUtils from '@/utils/editor.utils'
import stateTreeUtils from '@/utils/state-tree.utils'
import channelModule from '@/store/modules/channel.module'
import variablesModule from '@/store/modules/variables.module'
import MultiBotItemSelector from '@/components/common/MultiBotItemSelector.vue'

export default {
  name: 'Event',

  metaInfo() {
    const eventName = this.event?.name || ''
    return {
      title: [eventName, 'Events'].join(' | ')
    }
  },

  components: {
    AppHeader,
    AppHeaderBotControl,
    AppHeaderItemControl,
    ChatPreview,
    DeleteEventIcon,
    ConfirmDialog,
    DeleteDialog,
    EditorSidebar,
    ListSection,
    ShadowedWrapper,
    SideListMenu,
    EventSaveButton,
    WithLoader,
    MultiBotItemSelector
  },

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  watch: {
    botId: {
      handler: async function (botId, previousBotId) {
        if (this.$route.params.eventId === ITEM_IDS.NEW) return

        const currentEventMultiBotItemId = this.event?.multiBotItemId
        await botModule.whenBotsReady()
        await this.loadEventList(botId)
        if (isEmpty(this.eventList)) return

        await this.loadTemplateList(botModule.getPrimaryBotId(botId))
        const isVariantSwitch = botModule.getPrimaryBotId(botId) === botModule.getPrimaryBotId(previousBotId)
        const eventIdFromVariant = isVariantSwitch && currentEventMultiBotItemId ?
          this.eventList.find(event => event.multiBotItemId === currentEventMultiBotItemId)?.id :
          null
        const eventId = eventIdFromVariant || head(this.eventList).id
        await this.initEvent(eventId, botId)
        await this.initEmailTemplate()
      }
    },
    '$route.params.eventId': {
      handler: async function (eventId) {
        if (!eventId || eventId === this.eventId) return
        if (eventId === ITEM_IDS.NEW) return
        if (isEmpty(this.eventList)) return

        await botModule.whenBotsReady()
        await this.initEvent(eventId, this.$route.params.botId)
      }
    }
  },

  data() {
    return {
      eventList: [],
      eventVariantList: [],
      event: null,
      eventId: this.$route.params.eventId,
      isCopied: false,
      isRenameModeInAppHeader: false,
      loaders: {
        conversationJsons: false,
        name: false,
        items: true
      },
      ROUTE_NAMES,
      isDeleteDialogShown: false,
      isConfirmDialogShown: false,
      flagsImage,
      noEventsImage,
      itemToDelete: null,
      itemToEnableAutoTranslate: null,
      ITEM_IDS,
      renamedId: null,
      BUSY_BUTTONS
    }
  },

  computed: {
    isSaved: previewModule.isSaved,
    botEmailConfig: previewModule.botEmailConfig,
    stateTree: chatBubbleModule.stateTree,
    conversationType: conversationModule.getType,
    botList: botModule.getBotList,
    botVariantList: botModule.getBotVariantList,
    templateList: templateModule.templateList,

    deleteTitle() {
      return DELETE_TEXT[this.conversationType]?.title || ''
    },

    deleteDescription() {
      return DELETE_TEXT[this.conversationType]?.getDescription(this.itemToDelete?.name) || ''
    },

    deleteConfirmText() {
      return DELETE_TEXT[this.conversationType]?.confirm || ''
    },

    enableAutoTranslateMarketName() {
      return this.botVariantList.find(botVariant => botVariant.primaryBotId === this.botId)?.name || ''
    },

    enableAutoTranslateTitle() {
      return ENABLE_AUTO_TRANSLATE_TEXT.title
    },

    enableAutoTranslateDescription() {
      const eventName = this.itemToEnableAutoTranslate?.name || ''
      return ENABLE_AUTO_TRANSLATE_TEXT.getDescription(eventName, this.enableAutoTranslateMarketName, 'event')
    },

    enableAutoTranslateConfirmText() {
      return ENABLE_AUTO_TRANSLATE_TEXT.confirm
    },

    addButtonOptions() {
      return dropdownButtonsUtils.getEventOptions(this.eventList)
    },

    isEventIdLoaded() {
      return this.eventList.some(({ id }) => id === this.$route.params.eventId)
    },

    isNewEvent() {
      return this.event?.id === ITEM_IDS.NEW
    },

    isPrimaryVariant() {
      return botModule.isPrimaryVariant(this.botId)
    },

    inheritsConversation() {
      return this.event?.inheritsConversation || false
    },

    routeProps() {
      const routeProps = {
        itemIdName: 'eventId',
        route: ROUTE_NAMES.events,
        name: ROUTE_NAMES.events
      }
      if (this.event?.type === EVENT_TYPES.CUSTOM_EVENT) routeProps.query = { event_type: this.event.type }

      return routeProps
    }
  },

  methods: {
    isEmpty,

    async detachEvent() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.EDIT_MARKET, true)
      try {
        const updatedEvent = await eventService.update(this.botId, this.event.id, {
          inherit_conversation: false,
          conversation_json: this.event.conversationJson
        })
        await this.processEvent(updatedEvent)
      } catch (err) {
        toaster.error(err)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.EDIT_MARKET, false)
      }
    },

    async attachEvent(event) {
      try {
        const updatedEvent = await eventService.update(this.botId, event.id, {
          inherit_conversation: true
        })
        this.processEvent(updatedEvent)
      } catch (err) {
        toaster.error(err)
      }
    },

    changeBot(bot) {
      this.updateRouteEventId(this.eventId, bot.id)
      if (!this.isSaved) return

      botModule.setBot(bot)
      channelModule.setChannelList(bot.id)
    },

    updateRouteEventId(eventId, botId = this.$route.params.botId || this.botId, query = { event_type: this.event?.type }) {
      editorUtils.updateRouteByItemId(ITEM_NAMES.EVENT, { eventId, botId }, query)
    },

    resolveEventIdFromRoute() {
      const routeEventId = this.$route.params.eventId
      const routeMatch = this.eventList.find(event => event.id === routeEventId)
      if (routeMatch) return routeMatch.id

      return this.event?.id || head(this.eventList).id
    },

    async selectEventVariant(eventId, botId) {
      this.updateRouteEventId(eventId, botId)
      await this.initEvent(eventId, botId)
    },

    async initEvent(eventId, botId = this.$route.params.botId || this.botId) {
      const event = await eventService.get(botId, eventId)
      await this.processEvent(event)
      this.updateRouteEventId(eventId, botId, { event_type: event.type })
    },

    async createNewEvent(eventIndex) {
      const primaryBotId = botModule.getPrimaryBotId(this.botId)
      const needsMarketSwitch = primaryBotId !== this.botId

      this.updateRouteEventId(ITEM_IDS.NEW, primaryBotId)
      if (!this.isSaved) return

      if (needsMarketSwitch) {
        await Promise.all([
          this.loadEventList(primaryBotId),
          this.loadTemplateList(primaryBotId)
        ])
      }

      const { title, description, type } = this.addButtonOptions[eventIndex]

      const newEvent = { botId: primaryBotId, name: title, id: ITEM_IDS.NEW, description, type }
      await this.processEvent(newEvent)
      newEvent.conversationJsons = conversationModule.getJsons()
      this.eventList.unshift(newEvent)

      if (type === EVENT_TYPES.CUSTOM_EVENT) this.renamedId = ITEM_IDS.NEW
      stateTreeUtils.selectNode(this.stateTree)
    },

    cancelNewEvent() {
      if (!this.isSaved) return

      this.eventList.shift()
      const eventId = head(this.eventList).id
      this.selectEvent(eventId)
    },

    cancelDelete() {
      this.itemToDelete = null
      this.isDeleteDialogShown = false
    },

    handleRequestDelete(item) {
      this.itemToDelete = item
      this.isDeleteDialogShown = true
    },

    handleRequestEnableAutoTranslate(event) {
      this.itemToEnableAutoTranslate = event
      this.isConfirmDialogShown = true
    },

    cancelEnableAutoTranslate() {
      this.itemToEnableAutoTranslate = null
      this.isConfirmDialogShown = false
    },

    async confirmEnableAutoTranslate(event) {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ENABLE_AUTO_TRANSLATE, true)
      try {
        await this.attachEvent(event)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ENABLE_AUTO_TRANSLATE, false)
        this.cancelEnableAutoTranslate()
      }
    },

    async deleteEvent(event) {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      try {
        await eventService.delete(this.botId, event.id)
        const newEventId = editorUtils.resolveNewItemId(event.id, this.eventList)
        this.eventList = this.eventList.filter(({ id }) => id !== event.id)

        if (newEventId) {
          this.eventId = newEventId
          await this.selectEvent(this.eventId)
        } else {
          await this.$router.replace({ name: ROUTE_NAMES.events, params: { botId: this.botId } })
        }

        toaster.success(`Event "${event.name}" has been deleted.`)
      } catch (e) {
        toaster.error(e)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
        this.itemToDelete = null
        this.isDeleteDialogShown = false
      }
    },

    isCustomEvent(eventType) {
      return !values(EVENT_TYPES).includes(eventType)
    },

    async loadEventList(botId) {
      this.loaders.items = true
      this.eventList = sort((a, b) => a.name.localeCompare(b.name), await eventService.list(botId))
      this.loaders.items = false
    },

    menuBuilder(event) {
      const menuOptions = []
      if (this.isCustomEvent(event.type)) menuOptions.push(MENU_ITEMS.rename, MENU_ITEMS.copy)
      if (event.id !== ITEM_IDS.NEW) {
        if (!this.isPrimaryVariant && event.multiBotItemId && !event.inheritsConversation) {
          menuOptions.push(MENU_ITEMS.enableAutoTranslate)
        }
        if (this.isPrimaryVariant) menuOptions.push(MENU_ITEMS.delete)
      }

      return menuOptions
    },

    async processEvent(event) {
      if (event.id === this.event?.id && event.conversationJsonHash === this.event?.conversationJsonHash) return

      this.loaders.conversationJsons = true
      try {
        conversationModule.setArchivedConversationJson(event.conversationJson)
        conversationModule.init(CONVERSATION_TYPES.EVENTS, event.id, event.conversationJson, event.conversationJsonHash)
        if (event.conversationJson) {
          const botId = botModule.isPrimaryVariant(this.botId) || event.inheritsConversation ? botModule.getPrimaryBotId() : this.botId
          const eventId = botModule.isPrimaryVariant(this.botId) ? event.multiBotItemId || event.id : event.id
          const [referencedTemplates] = await Promise.all([
            eventService.getReferencedConversations(botId, eventId),
            this.loadTemplateList(botId)
          ])
          await conversationModule.setTemplates(referencedTemplates)
        }
        this.eventId = event.id
        this.event = event

        this.eventVariantList = event.multiBotItemId ?
          await eventService.listEventVariants(botModule.getPrimaryBotId(this.botId), event.multiBotItemId) :
          []
      } catch (err) {
        toaster.error(err)
      } finally {
        this.loaders.conversationJsons = false
      }
    },

    async processNewEvent(event) {
      this.eventList[0] = event
      await this.processEvent(event)
    },

    async selectEvent(eventId) {
      if (this.loaders.conversationJsons || this.renamedId === eventId) return
      if (!this.isSaved || this.event.id === eventId) return

      this.isRenameModeInAppHeader = false
      const event = await eventService.get(this.botId, eventId)
      await this.processEvent(event)
    },

    async saveEventName(updatedEvent) {
      try {
        this.loaders.name = true
        const index = findIndex(propEq('id', updatedEvent.id))(this.eventList)
        if (this.eventList[index].name === updatedEvent.name) return

        this.eventList[index] = updatedEvent
        if (this.event.id === updatedEvent.id || this.isNewEvent) {
          this.event = updatedEvent
        }
        if (this.isNewEvent) return

        await eventService.update(this.botId, updatedEvent.id, { name: updatedEvent.name })
      } catch (err) {
        toaster.error(err)
      } finally {
        this.resetRenameMode()
        this.loaders.name = false
      }
    },

    async initEmailTemplate() {
      if (!botModule.isEmailBot()) return

      this.botEmailConfig = await emailConfigService.get(this.botId)
    },

    handleCopy(eventId) {
      this.isCopied = true
      this.$copyText(`window.amioWebchat.triggerEvent('${eventId}')`)
      setTimeout(() => {
        this.isCopied = false
      }, 3000)
    },

    handleRenameMode(e) {
      if (e.fromAppHeader) {
        this.isRenameModeInAppHeader = e.mode
        return
      }

      this.renamedId = e.id
    },

    resetRenameMode() {
      this.renamedId = null
      this.isRenameModeInAppHeader = false
    },

    confirmRouteChange(from) {
      if (isEmpty(this.eventList)) return true

      const wasPreviewChanged = editorUtils.detectPreviewChange(this.isNewEvent)
      // confirm() returns true or false based on which button the user has pressed
      if (wasPreviewChanged) {
        const userConfirmation = confirm(editorUtils.resolveConfirmText(this.isNewEvent, 'event'))
        this.isSaved = userConfirmation
        if (userConfirmation && from.params.eventId === ITEM_IDS.NEW) {
          this.eventList.shift()
        }

        return userConfirmation
      } else {
        this.isSaved = true
        return true
      }
    },

    async loadTemplateList(botId) {
      const templates = await templateService.list(botId)
      templateModule.setTemplateList(templates)
    }
  },

  async mounted() {
    window.addEventListener('beforeunload', e => editorUtils.onBeforeUnload(e, this.isNewEvent))

    await botModule.whenBotsReady()

    await Promise.all([
      variablesModule.fetchVariables(),
      this.loadTemplateList(this.botId),
      this.loadEventList(this.botId)
    ])
    if (isEmpty(this.eventList)) return

    const eventId = this.resolveEventIdFromRoute()
    await this.initEvent(eventId, this.botId)
    await this.initEmailTemplate()
  },

  destroyed() {
    window.removeEventListener('beforeunload', e => editorUtils.onBeforeUnload(e, this.isNewEvent))
    this.event = null
    conversationModule.reset()
    conversationModule.resetArchivedConversationJson()
  },

  beforeRouteUpdate(to, from, next) {
    const routeChanged = this.confirmRouteChange(from)
    return routeChanged ? next() : next(false)
  },

  beforeRouteLeave(to, from, next) {
    const routeChanged = this.confirmRouteChange(from)
    return routeChanged ? next() : next(false)
  }
}
</script>
